import { Checkbox } from "@mui/material";
import { CheckboxIcon } from "@shared/frontend/icons";
import type { Row } from "@tanstack/react-table";
import React, { useCallback, useEffect, useRef } from "react";
import { useHyperTable } from "../../HyperTable";
import {
  NonEntityColumn,
  type NonEntityColumnProps,
} from "../../NonEntityColumn";
import SelectRowCell from "./SelectRowCell";

type HyperTableSelectRowColumnProps<Entity> = Omit<
  Partial<NonEntityColumnProps<Entity>>,
  "valueConverter"
> & {
  onItemChange?: (selected: Entity, isSelected: boolean) => void;
  onSelectAllChange?: (selected: boolean) => void;
  selectChildren?: boolean;
  selectAllBehavior?: "cross-page" | "single-page";
};

export const HyperTableSelectRowColumn = <Entity extends any>(
  props: HyperTableSelectRowColumnProps<Entity>,
) => {
  const {
    onItemChange,
    onSelectAllChange,
    selectChildren = true,
    selectAllBehavior = "cross-page",
    ...override
  } = props;
  const {
    table,
    allSelected,
    setAllSelected,
    selectedIds,
    tableSettings,
    findResult,
    pagination,
  } = useHyperTable<Entity>();
  const allSelectedRef = useRef<boolean>(allSelected); // Used to sync between the allSelected state and the table's selected columns state
  allSelectedRef.current = allSelected; // Ensure, that on each render the ref and state sync

  const handleRowChange = useCallback(
    (row: Row<Entity>, checked: boolean) => {
      setAllSelected(false);
      row.toggleSelected(checked, { selectChildren });
      onItemChange?.(row.original, checked);
    },
    [setAllSelected, onItemChange],
  );

  const handleAllChange = useCallback(
    (checked: boolean) => {
      table.toggleAllRowsSelected(checked);

      onSelectAllChange?.(checked);
      if (selectAllBehavior === "cross-page") {
        allSelectedRef.current = checked;
        setAllSelected(checked);
      } else {
        table
          .getFilteredRowModel()
          .flatRows.forEach((r) => onItemChange?.(r.original, checked));
      }
    },
    [table, onSelectAllChange, selectAllBehavior, setAllSelected],
  );

  useEffect(() => {
    if (
      selectedIds.length === findResult.totalRows &&
      !tableSettings.getSubRows &&
      selectAllBehavior === "cross-page"
    ) {
      setAllSelected(true);
      onSelectAllChange?.(true);
    }
  }, [selectedIds]);

  useEffect(() => {
    if (allSelected && selectAllBehavior === "cross-page") {
      table.toggleAllRowsSelected(true);
    }
  }, [pagination]);

  return (
    <NonEntityColumn
      header={() => (
        <Checkbox
          name="select_column_header"
          sx={{
            width: "100%",
            padding: 0,
          }}
          disableTouchRipple
          icon={<CheckboxIcon />}
          checkedIcon={<CheckboxIcon checked />}
          checked={
            allSelectedRef.current ||
            (selectAllBehavior === "single-page" &&
              table.getIsAllPageRowsSelected())
          }
          onChange={(_, value) => handleAllChange(!!value)}
          aria-label="Select all"
        />
      )}
      cell={(props) => (
        <SelectRowCell
          {...props}
          checked={allSelectedRef.current || props.row.getIsSelected()}
          onChange={(v) => handleRowChange(props.row, v)}
        />
      )}
      deps={[allSelected, selectAllBehavior, handleRowChange, handleAllChange]}
      enableSorting={false}
      enablePinning={false}
      enableResizing={false}
      size={50}
      minSize={50}
      maxSize={50}
      dontShowHeaderDivider={true}
      preventRowClick={true}
      defaultPinned
      {...override}
    />
  );
};
